// src/services/UserService.js
import auditService from './AuditService';
import storage from './StorageAdapter';
import { t } from '../useTranslations';
import { getConfigSync } from '../api/config';
import * as usersApi from '../api/users';

// LIC-106: enforcement för maxUsers från backend-licens.
// Returnerar { allowed, reason, max, current } där allowed=false betyder att
// nästa addUser ska blockeras. Om backend ej nåbar eller licens är missing/
// invalid → tillåt (failsafe — vi vill aldrig låsa ut admin).
export function checkUserSeatLimit(currentActiveCount) {
  const cfg = getConfigSync();
  if (!cfg || !cfg.license) return { allowed: true };
  const lic = cfg.license;
  const enforcing = lic.state === 'valid' || lic.state === 'expired_grace';
  if (!enforcing) return { allowed: true };
  if (typeof lic.maxUsers !== 'number' || lic.maxUsers <= 0) return { allowed: true };
  if (currentActiveCount < lic.maxUsers) return { allowed: true, max: lic.maxUsers, current: currentActiveCount };
  return {
    allowed: false,
    reason: 'license_seat_limit',
    max: lic.maxUsers,
    current: currentActiveCount,
  };
}

// Permission levels: view (read-only), edit (create/modify), admin (full control incl. delete/configure)
const PERMISSION_LEVELS = ['view', 'edit', 'admin'];

const ROLE_PERMISSION_PRESETS = {
  admin: { Dashboard: 'admin', GeoRisk: 'admin', CustomerRisk: 'admin', BWRA: 'admin', Admin: 'admin', TransactionMonitoring: 'admin', PeriodicReporting: 'admin' },
  analyst: { Dashboard: 'view', GeoRisk: 'edit', CustomerRisk: 'edit', BWRA: 'edit', Admin: 'view', TransactionMonitoring: 'edit', PeriodicReporting: 'edit' },
  viewer: { Dashboard: 'view', GeoRisk: 'view', CustomerRisk: 'view', BWRA: 'view', Admin: 'view', TransactionMonitoring: 'view', PeriodicReporting: 'view' }
};

// Built-in roles stored as role objects (these are the defaults seeded in storage)
const INITIAL_ROLES = [
  { id: 'admin', label: 'Admin', isBuiltIn: true, permissions: { Dashboard: 'admin', GeoRisk: 'admin', CustomerRisk: 'admin', BWRA: 'admin', Admin: 'admin', TransactionMonitoring: 'admin', PeriodicReporting: 'admin' } },
  { id: 'analyst', label: 'Analyst', isBuiltIn: true, permissions: { Dashboard: 'view', GeoRisk: 'edit', CustomerRisk: 'edit', BWRA: 'edit', Admin: 'view', TransactionMonitoring: 'edit', PeriodicReporting: 'edit' } },
  { id: 'viewer', label: 'Viewer', isBuiltIn: true, permissions: { Dashboard: 'view', GeoRisk: 'view', CustomerRisk: 'view', BWRA: 'view', Admin: 'view', TransactionMonitoring: 'view', PeriodicReporting: 'view' } }
];

const BUILT_IN_ROLE_IDS = ['admin', 'analyst', 'viewer'];

const INITIAL_USERS = [
  {
    id: 'usr-001',
    firstName: 'Simon',
    lastName: 'Sani',
    email: 'simon@mertil.se',
    role: 'admin',
    status: 'active',
    lastLogin: '2026-02-23T09:15:00',
    created: '2025-01-15T08:00:00',
    modules: ['Dashboard', 'GeoRisk', 'CustomerRisk', 'BWRA', 'Admin', 'TransactionMonitoring', 'PeriodicReporting'],
    permissions: { Dashboard: 'admin', GeoRisk: 'admin', CustomerRisk: 'admin', BWRA: 'admin', Admin: 'admin', TransactionMonitoring: 'admin', PeriodicReporting: 'admin' },
    entityAccess: 'all',
    entityRoles: {}
  },
  {
    id: 'usr-002',
    firstName: 'Anna',
    lastName: 'Lindberg',
    email: 'anna.lindberg@prime-rms.local',
    role: 'analyst',
    status: 'active',
    lastLogin: '2026-02-22T14:30:00',
    created: '2025-03-10T10:00:00',
    modules: ['Dashboard', 'GeoRisk', 'CustomerRisk'],
    permissions: { Dashboard: 'view', GeoRisk: 'edit', CustomerRisk: 'edit' },
    entityAccess: 'all',
    entityRoles: {}
  },
  {
    id: 'usr-003',
    firstName: 'Erik',
    lastName: 'Johansson',
    email: 'erik.johansson@prime-rms.local',
    role: 'viewer',
    status: 'active',
    lastLogin: '2026-02-20T11:00:00',
    created: '2025-06-01T12:00:00',
    modules: ['Dashboard', 'GeoRisk'],
    permissions: { Dashboard: 'view', GeoRisk: 'view' },
    entityAccess: 'all',
    entityRoles: {}
  },
  {
    id: 'usr-004',
    firstName: 'Maria',
    lastName: 'Svensson',
    email: 'maria.svensson@prime-rms.local',
    role: 'analyst',
    status: 'inactive',
    lastLogin: '2026-01-15T09:00:00',
    created: '2025-04-20T08:00:00',
    modules: ['Dashboard', 'CustomerRisk'],
    permissions: { Dashboard: 'view', CustomerRisk: 'edit' },
    entityAccess: 'all',
    entityRoles: {}
  },
  {
    id: 'usr-005',
    firstName: 'Lars',
    lastName: 'Nilsson',
    email: 'lars.nilsson@prime-rms.local',
    role: 'admin',
    status: 'active',
    lastLogin: '2026-02-21T16:45:00',
    created: '2025-02-01T09:00:00',
    modules: ['Dashboard', 'GeoRisk', 'CustomerRisk', 'BWRA', 'Admin', 'TransactionMonitoring', 'PeriodicReporting'],
    permissions: { Dashboard: 'admin', GeoRisk: 'admin', CustomerRisk: 'admin', BWRA: 'admin', Admin: 'admin', TransactionMonitoring: 'admin', PeriodicReporting: 'admin' },
    entityAccess: 'all',
    entityRoles: {}
  }
];

// Mapping between UserService module names and route-based module IDs
const MODULE_NAME_TO_ID = {
  'Dashboard': 'dashboard',
  'GeoRisk': 'geographical-risk',
  'CustomerRisk': 'customer-risk',
  'BWRA': 'bwra',
  'Admin': 'admin',
  'TransactionMonitoring': 'transaction-monitoring',
  'PeriodicReporting': 'periodic-reporting'
};

// Module name lists derived from MODULE_NAME_TO_ID — single source of truth for admin components
const PERMISSION_MODULE_NAMES = Object.keys(MODULE_NAME_TO_ID); // ['Dashboard', 'GeoRisk', 'CustomerRisk', 'BWRA', 'Admin']
const AUDIT_MODULE_NAMES = [...PERMISSION_MODULE_NAMES, 'Auth', 'DataSources', 'FieldMappings']; // includes Auth for login/logout, DataSources and FieldMappings for data source/mapping audit entries

class UserService {
  constructor() {
    this.USERS_KEY = 'admin_users';
    this.ROLES_KEY = 'admin_roles';
    this.listeners = [];
  }

  // Internal: loads ALL users including soft-deleted. Used by mutating methods and formatDisplayName.
  _loadAllUsers() {
    const users = storage.getJSON(this.USERS_KEY, null);
    if (users === null) {
      storage.setJSON(this.USERS_KEY, INITIAL_USERS);
      return [...INITIAL_USERS];
    }
    return this._migratePermissions(users);
  }

  // Public: returns only active + inactive users (excludes soft-deleted)
  getUsers() {
    return this._loadAllUsers().filter(u => u.status !== 'deleted');
  }

  async saveUsers(users) {
    await storage.setJSON(this.USERS_KEY, users);
    this.notifyListeners();
  }

  // Get display name for a user (firstName + lastName) - always uses firstLast format
  getDisplayName(email) {
    return this.formatDisplayName(email, 'firstLast');
  }

  // Format display name based on configured format.
  // identifier can be an email or a first name (backward compat with legacy data like 'simon')
  // Searches ALL users including soft-deleted to preserve audit trail context.
  formatDisplayName(identifier, format = 'firstLast') {
    if (!identifier || identifier === 'unknown') return identifier || '';
    const users = this._loadAllUsers();
    // Try to find by email first
    let user = users.find(u => u.email === identifier);
    // Fallback: try to find by firstName or email prefix (for legacy identifiers)
    if (!user) {
      const lower = identifier.toLowerCase();
      user = users.find(u =>
        u.firstName?.toLowerCase() === lower ||
        u.email?.split('@')[0]?.toLowerCase() === lower
      );
    }
    if (!user) return identifier;

    let name;
    switch (format) {
      case 'lastFirst':
        if (user.firstName && user.lastName) name = `${user.lastName}, ${user.firstName}`;
        else if (user.firstName) name = user.firstName;
        else if (user.lastName) name = user.lastName;
        else name = user.email || identifier;
        break;
      case 'email':
        name = user.email || identifier;
        break;
      case 'firstLast':
      default:
        if (user.firstName && user.lastName) name = `${user.firstName} ${user.lastName}`;
        else if (user.firstName) name = user.firstName;
        else if (user.lastName) name = user.lastName;
        else name = user.email || identifier;
    }

    // Append deleted annotation for audit trail clarity
    if (user.status === 'deleted') {
      name = `${name} ${t('common.deletedUserSuffix')}`;
    }

    return name;
  }

  async addUser(userData) {
    const allUsers = this._loadAllUsers();
    // LIC-106: räkna bara aktiva (icke-soft-deleted) mot licens-cap.
    // Soft-delete:ade behåller historik men tar inte upp seat.
    const activeCount = allUsers.filter(u => u.status !== 'deleted').length;
    const seatCheck = checkUserSeatLimit(activeCount);
    if (!seatCheck.allowed) {
      const err = new Error(t('userService.errorSeatLimitReached', {
        max: seatCheck.max,
        current: seatCheck.current,
      }));
      err.code = 'license_seat_limit';
      err.maxUsers = seatCheck.max;
      err.currentUsers = seatCheck.current;
      // Audit-logga blockerat försök för spårbarhet
      auditService.log('BLOCKED', 'Admin', {
        entityType: 'user',
        entityId: userData.email || 'unknown',
        reason: 'license_seat_limit',
        max: seatCheck.max,
        current: seatCheck.current,
      });
      throw err;
    }

    // Compute next ID based on all users (including soft-deleted) to avoid collisions
    const maxNum = allUsers.reduce((max, u) => {
      const num = parseInt((u.id || '').replace('usr-', ''), 10);
      return isNaN(num) ? max : Math.max(max, num);
    }, 0);
    const nextId = `usr-${String(maxNum + 1).padStart(3, '0')}`;
    const newUser = {
      id: nextId,
      ...userData,
      lastLogin: null,
      created: new Date().toISOString()
    };
    allUsers.push(newUser);
    await this.saveUsers(allUsers);
    auditService.log('CREATE', 'Admin', {
      entityType: 'user',
      entityId: newUser.email,
      name: `${newUser.firstName || ''} ${newUser.lastName || ''}`.trim()
    });
    // INFRA-112 dual-write: synca till backend fire-and-forget. Backend-state
    // blir source-of-truth; localStorage är snabb läs-cache. Failure blockerar
    // inte operativt flöde — nästa syncFromBackend fångar drift.
    this._syncUserToBackend('create', newUser).catch((err) => {
      console.warn('[UserService] backend-sync create failed:', err?.message);
    });
    return newUser;
  }

  async updateUser(userId, updatedFields) {
    const users = this._loadAllUsers();
    const index = users.findIndex(u => u.id === userId);
    if (index >= 0) {
      users[index] = { ...users[index], ...updatedFields };
      await this.saveUsers(users);
      auditService.log('UPDATE', 'Admin', {
        entityType: 'user',
        entityId: users[index].email,
        name: `${users[index].firstName || ''} ${users[index].lastName || ''}`.trim()
      });
      this._syncUserToBackend('update', users[index]).catch((err) => {
        console.warn('[UserService] backend-sync update failed:', err?.message);
      });
    }
    return this.getUsers();
  }

  // Soft-delete: marks user as status:'deleted' instead of permanent removal.
  // This preserves audit trail integrity – deleted users' names still appear in historical logs.
  async deleteUser(userId) {
    const users = this._loadAllUsers();
    const user = users.find(u => u.id === userId);
    if (user) {
      user.status = 'deleted';
      await this.saveUsers(users);
      auditService.log('DELETE', 'Admin', {
        entityType: 'user',
        entityId: user.email,
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim()
      });
      this._syncUserToBackend('delete', user).catch((err) => {
        console.warn('[UserService] backend-sync delete failed:', err?.message);
      });
    }
    return this.getUsers();
  }

  // ------------------------------------------------------------------
  // INFRA-112 — backend sync (dual-write + initial pull).
  // Strategi: localStorage är fortsatt snabb läs-cache. Backend är den
  // auktoritativa källan över tid. Alla skriv-ops dual-writas.
  // ------------------------------------------------------------------

  /**
   * Synca en lokal user till backend. Emailet är den stabila nyckeln.
   * Om user finns serverside via findByEmail → updateUser, annars createUser.
   * Lokala usr-XXX-IDn kartas inte till UUID:er — vi stannar vid email-lookup
   * för att hålla implementation enkel under dual-write-fas.
   */
  async _syncUserToBackend(op, user) {
    if (!user || !user.email) return;
    try {
      let backendUser = null;
      try {
        backendUser = await usersApi.getUserByEmail(user.email);
      } catch (err) {
        if (err?.status !== 404) throw err;
      }

      if (op === 'delete') {
        if (backendUser) await usersApi.deleteUser(backendUser.id);
        return;
      }

      const payload = {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role,
        status: user.status || 'active',
        modules: user.modules,
        permissions: user.permissions || {},
        entityAccess: user.entityAccess ?? 'all',
      };

      if (backendUser) {
        await usersApi.updateUser(backendUser.id, payload);
      } else {
        await usersApi.createUser(payload);
      }
    } catch (err) {
      // Swallow — dual-write är fire-and-forget. Nästa initFromBackend ritar om.
      throw err;
    }
  }

  /**
   * Kallas från App.js vid login/mount. Pullar users från backend och
   * merger in i localStorage så UI speglar backend-state.
   */
  async initFromBackend() {
    try {
      const backendUsers = await usersApi.listUsers();
      if (!Array.isArray(backendUsers) || backendUsers.length === 0) {
        // Backend tom — pusha upp alla lokala users via dual-write
        const local = this._loadAllUsers();
        for (const u of local) {
          try { await this._syncUserToBackend('create', u); } catch (_) {}
        }
        return;
      }

      // Backend har data — merge: backend vinner för existing, local-only
      // users pushas upp.
      const localUsers = this._loadAllUsers();
      const byEmailLocal = new Map(localUsers.map(u => [u.email?.toLowerCase(), u]));
      const byEmailBackend = new Map(backendUsers.map(u => [u.email?.toLowerCase(), u]));

      // Import backend users to local (keep local usr-XXX-id om finns, annars använd backend UUID)
      const merged = [];
      for (const b of backendUsers) {
        const local = byEmailLocal.get(b.email?.toLowerCase());
        merged.push({
          id: local?.id || b.id,
          email: b.email,
          firstName: b.firstName,
          lastName: b.lastName,
          role: b.role,
          status: b.status,
          modules: b.modules,
          permissions: b.permissions || {},
          entityAccess: b.entityAccess ?? 'all',
          lastLogin: b.lastLogin || local?.lastLogin || null,
          created: b.createdAt || local?.created,
        });
      }

      // Push lokala som inte finns i backend
      for (const l of localUsers) {
        if (!byEmailBackend.has(l.email?.toLowerCase())) {
          merged.push(l);
          try { await this._syncUserToBackend('create', l); } catch (_) {}
        }
      }

      await this.saveUsers(merged);
    } catch (err) {
      // Backend onåbart / ej inloggad — behåll lokal state oförändrad
      console.debug('[UserService] initFromBackend skipped:', err?.message);
    }
  }

  // Import multiple users from parsed CSV rows.
  // Each row: { firstName, lastName, email, role, entityAccess }
  // Returns { imported: number, skipped: [] } — skipped contains rows with errors.
  async importUsers(rows) {
    const allUsers = this._loadAllUsers();
    const existingEmails = new Set(allUsers.map(u => (u.email || '').toLowerCase()));
    const validRoles = this.getRoles().map(r => r.id);
    const imported = [];
    const skipped = [];

    rows.forEach((row, idx) => {
      const email = (row.email || '').trim().toLowerCase();
      if (!email) { skipped.push({ row: idx + 1, reason: 'missingEmail', data: row }); return; }
      if (existingEmails.has(email)) { skipped.push({ row: idx + 1, reason: 'duplicateEmail', data: row }); return; }
      const roleId = (row.role || '').trim().toLowerCase();
      if (!validRoles.includes(roleId)) { skipped.push({ row: idx + 1, reason: 'invalidRole', data: row }); return; }

      const preset = ROLE_PERMISSION_PRESETS[roleId] || ROLE_PERMISSION_PRESETS.viewer;
      const modules = Object.keys(preset);
      const maxNum = [...allUsers, ...imported].reduce((max, u) => {
        const num = parseInt((u.id || '').replace('usr-', ''), 10);
        return isNaN(num) ? max : Math.max(max, num);
      }, 0);
      const nextId = `usr-${String(maxNum + 1).padStart(3, '0')}`;

      let entityAccess = 'all';
      if (row.entityAccess && row.entityAccess.trim() !== '' && row.entityAccess.trim().toLowerCase() !== 'all') {
        entityAccess = row.entityAccess.split(';').map(s => s.trim()).filter(Boolean);
      }

      const newUser = {
        id: nextId,
        firstName: (row.firstName || '').trim(),
        lastName: (row.lastName || '').trim(),
        email,
        role: roleId,
        status: 'active',
        lastLogin: null,
        created: new Date().toISOString(),
        modules,
        permissions: { ...preset },
        entityAccess,
        entityRoles: {}
      };
      imported.push(newUser);
      existingEmails.add(email);
    });

    if (imported.length > 0) {
      const updatedAll = [...allUsers, ...imported];
      await this.saveUsers(updatedAll);
      imported.forEach(u => {
        auditService.log('CREATE', 'Admin', {
          entityType: 'user',
          entityId: u.email,
          name: `${u.firstName} ${u.lastName}`.trim(),
          source: 'csv_import'
        });
      });
      // ADM-209: dual-write varje importerad user till backend (rms_users).
      // Fire-and-forget per rad — fel på en user blockerar inte de andra.
      // Nästa initFromBackend skulle fånga drift, men explicit sync ger snabbare
      // cross-device-konsistens.
      for (const u of imported) {
        this._syncUserToBackend('create', u).catch((err) => {
          console.warn('[UserService] importUsers backend-sync failed:', u.email, err?.message);
        });
      }
    }

    return { imported: imported.length, skipped };
  }

  async toggleStatus(userId) {
    const users = this._loadAllUsers();
    const index = users.findIndex(u => u.id === userId && u.status !== 'deleted');
    if (index >= 0) {
      users[index] = {
        ...users[index],
        status: users[index].status === 'active' ? 'inactive' : 'active'
      };
      await this.saveUsers(users);
    }
    return this.getUsers();
  }

  // Get user's allowed module route IDs (e.g., ['dashboard', 'geographical-risk'])
  // email = the user's email address (login identifier)
  getUserAllowedModuleIds(email) {
    const users = this.getUsers();
    const user = users.find(u => u.email === email);
    if (!user || !user.modules) return null; // null = no restriction (user not found)
    return user.modules.map(name => MODULE_NAME_TO_ID[name]).filter(Boolean);
  }

  // Check if user has at least the required permission level for a module
  // Hierarchy: admin > edit > view
  hasPermission(email, moduleName, requiredLevel) {
    const users = this.getUsers();
    const user = users.find(u => u.email === email);
    if (!user) return false;
    if (!user.modules || !user.modules.includes(moduleName)) return false;
    const userLevel = (user.permissions && user.permissions[moduleName]) || 'view';
    const hierarchy = { view: 0, edit: 1, admin: 2 };
    return (hierarchy[userLevel] || 0) >= (hierarchy[requiredLevel] || 0);
  }

  // Check if user has a specific granular action permission for a module.
  // Falls back to level-based check when granular permissions are not defined.
  // action: 'read' | 'create' | 'edit' | 'delete' | 'approve'
  hasAction(email, moduleName, action) {
    const effectiveRoleId = this.getEffectiveRole(email, null);
    const role = this.getRoleById(effectiveRoleId);
    if (role && role.granularPermissions && role.granularPermissions[moduleName]) {
      return !!role.granularPermissions[moduleName][action];
    }
    // Fallback: derive from level
    const users = this.getUsers();
    const user = users.find(u => u.email === email);
    if (!user || !user.modules || !user.modules.includes(moduleName)) return false;
    const level = (user.permissions && user.permissions[moduleName]) || 'view';
    const levelActions = {
      view: ['read'],
      edit: ['read', 'create', 'edit'],
      admin: ['read', 'create', 'edit', 'delete', 'approve']
    };
    return (levelActions[level] || []).includes(action);
  }

  // Get entity IDs the user has access to. Returns 'all' or array of entity IDs.
  getUserEntityAccess(email) {
    const users = this.getUsers();
    const user = users.find(u => u.email === email);
    if (!user) return [];
    return user.entityAccess || 'all';
  }

  // Check if user has access to a specific entity
  hasEntityAccess(email, entityId) {
    const access = this.getUserEntityAccess(email);
    if (access === 'all') return true;
    return Array.isArray(access) && access.includes(entityId);
  }

  // Get filtered list of entities user can access
  getAccessibleEntities(email, allEntities) {
    const access = this.getUserEntityAccess(email);
    if (access === 'all') return allEntities;
    if (!Array.isArray(access)) return [];
    return allEntities.filter(e => access.includes(e.id));
  }

  // Get the effective role for a user in a specific entity context.
  // If the user has an entity-specific role override, returns that; otherwise returns the global role.
  getEffectiveRole(email, entityId) {
    const users = this.getUsers();
    const user = users.find(u => u.email === email);
    if (!user) return 'viewer';
    if (entityId && user.entityRoles && user.entityRoles[entityId]) {
      return user.entityRoles[entityId];
    }
    return user.role || 'viewer';
  }

  // Get effective permissions for a user in a specific entity context.
  // Resolves per-entity role override → role permissions, or falls back to user's module permissions.
  getEffectivePermissions(email, entityId) {
    const effectiveRoleId = this.getEffectiveRole(email, entityId);
    const roleObj = this.getRoleById(effectiveRoleId);
    if (roleObj && roleObj.permissions) return { ...roleObj.permissions };
    const users = this.getUsers();
    const user = users.find(u => u.email === email);
    const perms = {};
    (user?.modules || []).forEach(mod => {
      perms[mod] = (user.permissions && user.permissions[mod]) || 'view';
    });
    return perms;
  }

  // Get permissions for a user (with backward compat: default to 'view' if missing)
  getUserPermissions(email) {
    const users = this.getUsers();
    const user = users.find(u => u.email === email);
    if (!user) return {};
    const perms = {};
    (user.modules || []).forEach(mod => {
      perms[mod] = (user.permissions && user.permissions[mod]) || 'view';
    });
    return perms;
  }

  // === ROLE MANAGEMENT ===

  getRoles() {
    const roles = storage.getJSON(this.ROLES_KEY, null);
    if (roles === null) {
      storage.setJSON(this.ROLES_KEY, INITIAL_ROLES);
      return [...INITIAL_ROLES];
    }
    return this._migrateRoles(roles);
  }

  async saveRoles(roles) {
    await storage.setJSON(this.ROLES_KEY, roles);
    this.notifyListeners();
  }

  getRoleById(roleId) {
    const roles = this.getRoles();
    return roles.find(r => r.id === roleId) || INITIAL_ROLES.find(r => r.id === 'viewer');
  }

  isRoleInUse(roleId) {
    return this.getUsers().some(u => u.role === roleId);
  }

  async addRole(roleData) {
    const roles = this.getRoles();
    const id = `role_${Date.now()}`;
    const newRole = {
      id,
      label: roleData.label.trim(),
      isBuiltIn: false,
      permissions: { ...roleData.permissions }
    };
    if (roleData.granularPermissions) {
      newRole.granularPermissions = { ...roleData.granularPermissions };
    }
    roles.push(newRole);
    await this.saveRoles(roles);
    auditService.log('CREATE', 'Admin', { entityType: 'role', entityId: id, name: newRole.label });
    this._syncRoleToBackend('create', newRole).catch((e) => console.warn('[Role sync] create:', e?.message));
    return newRole;
  }

  async updateRole(roleId, updatedFields) {
    const roles = this.getRoles();
    const index = roles.findIndex(r => r.id === roleId);
    if (index < 0) return roles;
    const role = roles[index];
    if (role.isBuiltIn) {
      // Built-in: only allow updating permissions
      roles[index] = { ...role, permissions: updatedFields.permissions || role.permissions };
    } else {
      roles[index] = { ...role, label: updatedFields.label || role.label, permissions: updatedFields.permissions || role.permissions };
    }
    await this.saveRoles(roles);
    auditService.log('UPDATE', 'Admin', { entityType: 'role', entityId: roleId, name: roles[index].label });
    this._syncRoleToBackend('update', roles[index]).catch((e) => console.warn('[Role sync] update:', e?.message));
    return roles;
  }

  async deleteRole(roleId) {
    const roles = this.getRoles();
    const role = roles.find(r => r.id === roleId);
    if (!role) return { success: false, reason: 'notFound' };
    if (role.isBuiltIn) return { success: false, reason: 'builtin' };
    if (this.isRoleInUse(roleId)) return { success: false, reason: 'inUse' };
    const filtered = roles.filter(r => r.id !== roleId);
    await this.saveRoles(filtered);
    auditService.log('DELETE', 'Admin', { entityType: 'role', entityId: roleId, name: role.label });
    this._syncRoleToBackend('delete', role).catch((e) => console.warn('[Role sync] delete:', e?.message));
    return { success: true };
  }

  // INFRA-112: role backend-sync. Built-in-roller hoppar vi över eftersom
  // backend seedar dem via migration och blockerar ändring på route-nivå.
  async _syncRoleToBackend(op, role) {
    if (!role || role.isBuiltIn) return;
    const rolesApi = require('../api/roles');
    const payload = { id: role.id, label: role.label, permissions: role.permissions || {} };

    if (op === 'delete') {
      try { await rolesApi.deleteRole(role.id); } catch (_) { /* may not exist */ }
      return;
    }
    try {
      await rolesApi.updateRole(role.id, payload);
    } catch (err) {
      if (err?.status === 404) await rolesApi.createRole(payload);
      else throw err;
    }
  }

  // Ensure built-in roles are always present in stored roles
  _migrateRoles(roles) {
    let changed = false;
    const existingIds = new Set(roles.map(r => r.id));
    INITIAL_ROLES.forEach(builtIn => {
      if (!existingIds.has(builtIn.id)) {
        roles.push({ ...builtIn });
        changed = true;
      }
    });
    if (changed) {
      storage.setJSON(this.ROLES_KEY, roles);
    }
    return roles;
  }

  // Get role permission presets
  static getRolePresets() {
    return ROLE_PERMISSION_PRESETS;
  }

  static getPermissionLevels() {
    return PERMISSION_LEVELS;
  }

  // Migrate existing users without permissions field
  _migratePermissions(users) {
    let changed = false;
    users.forEach(user => {
      // Migrate old username/fullName → firstName/lastName/email format
      if (user.username !== undefined || user.fullName !== undefined) {
        if (!user.firstName && !user.lastName && user.fullName) {
          const parts = (user.fullName || '').split(' ');
          user.firstName = parts[0] || '';
          user.lastName = parts.slice(1).join(' ') || '';
        }
        // Keep email as-is (was already stored)
        // Special migration: simon's email → simon@mertil.se
        if (user.username === 'simon' && user.email === 'simon@prime-rms.local') {
          user.email = 'simon@mertil.se';
        }
        delete user.username;
        delete user.fullName;
        changed = true;
      }
      if (!user.permissions) {
        const preset = ROLE_PERMISSION_PRESETS[user.role] || ROLE_PERMISSION_PRESETS.viewer;
        user.permissions = {};
        (user.modules || []).forEach(mod => {
          user.permissions[mod] = preset[mod] || 'view';
        });
        changed = true;
      }
      // Migrate: add entityAccess if missing (backward compat → all)
      if (user.entityAccess === undefined) {
        user.entityAccess = 'all';
        changed = true;
      }
      // Migrate: add entityRoles if missing (backward compat → no overrides)
      if (user.entityRoles === undefined) {
        user.entityRoles = {};
        changed = true;
      }
      // Migrate ARB → BWRA in stored user data
      if (user.modules && user.modules.includes('ARB')) {
        user.modules = user.modules.map(m => m === 'ARB' ? 'BWRA' : m);
        if (user.permissions && user.permissions.ARB !== undefined) {
          user.permissions.BWRA = user.permissions.ARB;
          delete user.permissions.ARB;
        }
        changed = true;
      }
      // Migrate: add TransactionMonitoring to admin users who don't have it yet
      if (user.modules && user.modules.includes('Admin') && !user.modules.includes('TransactionMonitoring')) {
        user.modules.push('TransactionMonitoring');
        if (!user.permissions) user.permissions = {};
        const preset = ROLE_PERMISSION_PRESETS[user.role] || ROLE_PERMISSION_PRESETS.viewer;
        user.permissions.TransactionMonitoring = preset.TransactionMonitoring || 'view';
        changed = true;
      }
      // Migrate: add PeriodicReporting to admin users who don't have it yet
      if (user.modules && user.modules.includes('Admin') && !user.modules.includes('PeriodicReporting')) {
        user.modules.push('PeriodicReporting');
        if (!user.permissions) user.permissions = {};
        const preset = ROLE_PERMISSION_PRESETS[user.role] || ROLE_PERMISSION_PRESETS.viewer;
        user.permissions.PeriodicReporting = preset.PeriodicReporting || 'view';
        changed = true;
      }
      // Migrate: ensure simon@mertil.se always has full admin access
      if (user.email === 'simon@mertil.se' && user.status !== 'deleted') {
        const allModules = Object.keys(MODULE_NAME_TO_ID);
        const needsModuleUpdate = !user.modules || allModules.some(m => !user.modules.includes(m));
        const needsPermUpdate = !user.permissions || allModules.some(m => user.permissions[m] !== 'admin');
        const needsRoleUpdate = user.role !== 'admin' || user.status !== 'active';
        if (needsModuleUpdate || needsPermUpdate || needsRoleUpdate) {
          user.role = 'admin';
          user.status = 'active';
          user.modules = [...allModules];
          user.permissions = {};
          allModules.forEach(m => { user.permissions[m] = 'admin'; });
          user.entityAccess = 'all';
          changed = true;
        }
      }
    });
    if (changed) {
      storage.setJSON(this.USERS_KEY, users);
    }
    return users;
  }

  // === CHANGE LISTENERS ===

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notifyListeners() {
    this.listeners.forEach(listener => listener());
  }
}

const userService = new UserService();

if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.userService = userService;
}

const validatePassword = (password) => {
  const checks = {
    minLength: (password || '').length >= 8,
    hasUppercase: /[A-Z]/.test(password || ''),
    hasLowercase: /[a-z]/.test(password || ''),
    hasNumber: /[0-9]/.test(password || ''),
    hasSpecial: /[!@#$%^&*()\-_=+\[\]{};':"\\|,.<>/?`~]/.test(password || ''),
  };
  const isValid = Object.values(checks).every(Boolean);
  return { isValid, checks };
};

export { PERMISSION_LEVELS, ROLE_PERMISSION_PRESETS, BUILT_IN_ROLE_IDS, INITIAL_ROLES, PERMISSION_MODULE_NAMES, AUDIT_MODULE_NAMES, validatePassword };
export default userService;
